import React, { useState } from 'react';
import type { Todo } from '../types';
import { extractDatePart } from '../utils/dateUtils';

interface TodoFormSubtasksProps {
  children: Todo[];
  onSubtaskClick?: (todo: Todo) => void;
}

// Helper function to format date string for display without timezone issues
const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';

  // Parse the date string directly as local date to avoid timezone conversion
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const TodoFormSubtasks: React.FC<TodoFormSubtasksProps> = ({
  children,
  onSubtaskClick,
}) => {
  const [showChildren, setShowChildren] = useState(false);

  if (children.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold text-gray-700">
          Subtasks ({children.length})
        </label>
        <button
          type="button"
          onClick={() => setShowChildren(!showChildren)}
          className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
        >
          {showChildren ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              <span>Hide Subtasks</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <span>Show Subtasks</span>
            </>
          )}
        </button>
      </div>

      {showChildren && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          {children.map(child => (
            <div
              key={child.id}
              className={`bg-white rounded-md p-3 border border-gray-200 transition-all duration-200 ${
                onSubtaskClick ? 'cursor-pointer hover:border-blue-300 hover:shadow-md hover:bg-blue-50' : ''
              }`}
              onClick={() => onSubtaskClick?.(child)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className={`font-medium text-sm ${child.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'} ${
                    onSubtaskClick ? 'hover:text-blue-700' : ''
                  }`}>
                    {child.title}
                  </h4>
                  {child.description && (
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      {child.description}
                    </p>
                  )}
                  <div className="flex items-center space-x-3 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      child.status === 'completed' ? 'bg-green-100 text-green-800' :
                      child.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      child.status === 'waiting' ? 'bg-yellow-100 text-yellow-800' :
                      child.status === 'deferred' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {child.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    {child.horizon && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        child.horizon === 'now' ? 'bg-red-100 text-red-800' :
                        child.horizon === 'next' ? 'bg-blue-100 text-blue-800' :
                        child.horizon === 'soon' ? 'bg-yellow-100 text-yellow-800' :
                        child.horizon === 'later' ? 'bg-gray-100 text-gray-600' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {child.horizon}
                      </span>
                    )}
                    {child.deadline && (
                      <span className="text-xs text-gray-500">
                        Due: {formatDateForDisplay(extractDatePart(child.deadline))}
                      </span>
                    )}
                  </div>
                </div>
                {onSubtaskClick && (
                  <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500 italic">
            {onSubtaskClick ? 'Click on any subtask to edit it' : 'Click on individual subtasks in the main task list to edit them'}
          </p>
        </div>
      )}
    </div>
  );
};

export default TodoFormSubtasks;
